import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, type ReactNode } from 'react';

import { billingSummaryKey, fetchBillingSummary } from '@/shared/api';
import { PLANS, planHasFeature, type PlanFeature, type PlanTier } from '@/shared/config/plans';

import { PlanContext, type PlanContextValue } from './planContext';

/**
 * The plan the UI renders against: the signed-in master's, from
 * GET /master/billing. There is no setter — only the payment provider's
 * webhook changes a plan, so the screen cannot claim one nobody paid for.
 *
 * This observer never fetches (`enabled: false`); the dashboard layout does.
 * A public page therefore never asks the API who is signed in, and until the
 * summary arrives the tier is `initialTier` (Free), so a gate stays closed
 * rather than flashing open.
 *
 * Feature gates read `can()` rather than comparing tier strings, so adding a
 * tier does not mean hunting down equality checks across the app.
 */
export const PlanProvider = ({
  children,
  initialTier = 'free',
}: {
  children: ReactNode;
  initialTier?: PlanTier;
}) => {
  const { data } = useQuery({
    queryKey: billingSummaryKey,
    queryFn: fetchBillingSummary,
    enabled: false,
  });
  const summary = data ?? null;
  const tier = summary?.tier ?? initialTier;

  const can = useCallback(
    (feature: PlanFeature) =>
      summary ? summary.features.includes(feature) : planHasFeature(tier, feature),
    [summary, tier],
  );

  const value = useMemo<PlanContextValue>(
    () => ({ tier, plan: PLANS[tier], summary, can }),
    [tier, summary, can],
  );

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};
